import { setupAGGrid } from "@/lib/AGGrid/setupAGGrid";
import { ReportInvoicesTable } from "@/app/Reports/Reports/Components/ReportInvoices/ReportInvoicesTable";
import {
  ReportInvoiceListItem,
  reportInvoiceListQuery,
} from "@/app/Reports/Reports/Components/ReportInvoices/GraphQL/reportInvoicesQuery";
import { Suspense, useCallback, useRef } from "react";
import { Button } from "@/components/catalyst/button";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { getCurrencyConversionDisclaimer } from "@/app/Reports/Reports/Components/ReportInvoices/utils/currencyConversion";
import { AgGridReact } from "ag-grid-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

setupAGGrid();

export function ReportInvoicesScreen() {
  const gridRef = useRef<AgGridReact<ReportInvoiceListItem>>(null);

  const { data } = useGqlQuery(reportInvoiceListQuery, {
    rangeStartDate: "1900-01-01",
    rangeEndDate: "2099-12-31",
  });

  // Check if we have multiple currencies for the disclaimer
  const uniqueCurrencies = data?.reportInvoices
    ? [...new Set(data.reportInvoices.map((inv) => inv.currency))]
    : [];
  const hasMultipleCurrencies = uniqueCurrencies.length > 1;

  const onBtExport = useCallback(
    (type: "csv" | "xls") => () => {
      if (type === "csv") {
        gridRef.current?.api.exportDataAsCsv();
      } else {
        gridRef.current?.api.exportDataAsExcel();
      }
    },
    [],
  );

  return (
    <div className="flex h-full flex-col space-y-4 p-8">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/reports"
              params={(prev) => prev}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Invoices</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">
            Invoice Reports
          </h2>
          <p className="text-sm text-gray-500">
            View and export all invoice reports (~600 invoices). Use the table
            filters to narrow down results.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={onBtExport("csv")}>Export CSV</Button>
          <Button onClick={onBtExport("xls")}>Export excel</Button>
        </div>
      </div>

      {hasMultipleCurrencies ? (
        <div className="rounded-md bg-blue-50 p-4">
          <div className="text-sm text-blue-700">
            <strong>Note:</strong> {getCurrencyConversionDisclaimer()}
          </div>
        </div>
      ) : null}

      <div className="grow rounded-lg">
        <Suspense fallback={<Skeleton className="mt-3 h-80" />}>
          <ReportInvoicesTable ref={gridRef} />
        </Suspense>
      </div>
    </div>
  );
}
